import React from 'react';
import { GraduationCap, FlaskConical, Briefcase, Rocket } from 'lucide-react';

const SuccessFrameworkSection = () => {
  const steps = [
    { number: 1, icon: GraduationCap, title: "LEARN", headline: "Build a strong foundation", description: "You'll master the fundamentals of AI, Cloud, and DevOps through structured modules taught by industry mentors.", outcome: "You gain clarity and technical confidence.", tracking: "Each lesson updates automatically in your CareerLift Student Portal, showing progress % and skill badges earned." },
    { number: 2, icon: FlaskConical, title: "APPLY", headline: "Turn knowledge into action", description: "You'll work on guided, hands-on labs — building automations, deploying cloud systems, and using real-world tools.", outcome: "You can perform tasks employers actually test for.", tracking: "Quizzes, practice labs, and mentor evaluations appear in your dashboard for instant feedback." },
    { number: 3, icon: Briefcase, title: "BUILD", headline: "Create your professional portfolio", description: "You'll develop industry projects — like AI chatbots, CI/CD pipelines, or multi-cloud apps — all verified by your mentor.", outcome: "You graduate with proof of ability, not just theory.", tracking: "Your portfolio auto-syncs inside your Student Portal, ready to share on LinkedIn or interviews." },
    { number: 4, icon: Rocket, title: "LAUNCH", headline: "Land your dream IT role", description: "Our mentors and career team help you refine your resume, prepare for interviews, and connect with hiring networks.", outcome: "You transition from student to job-ready professional.", tracking: "You'll see your job readiness score, placement milestones, and mentorship sessions completed — all visible in your dashboard." }
  ];

  return (
    <section className="py-24 bg-gradient-to-b from-white via-indigo-50 to-white relative overflow-hidden">
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{ backgroundImage: 'radial-gradient(circle at 2px 2px, #4F46E5 1px, transparent 0)', backgroundSize: '40px 40px' }} />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center mb-16 scroll-reveal">
          <div className="inline-block mb-4">
            <span className="bg-indigo-600 text-white px-4 py-2 rounded-full text-sm font-semibold">🚀 THE CAREERLIFT FRAMEWORK</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">Your Path to Career Success</h2>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto leading-relaxed">From Student to Job-Ready Professional — A Clear 4-Step Journey</p>
        </div>

        <div className="space-y-12">
          {steps.map((step, index) => {
            const IconComponent = step.icon;
            const isEven = index % 2 === 0;
            return (
              <div key={step.number} className={`scroll-reveal flex flex-col ${isEven ? 'md:flex-row' : 'md:flex-row-reverse'} gap-8 items-center`}>
                <div className="flex-shrink-0">
                  <div className="relative">
                    <div className="w-32 h-32 bg-indigo-600 rounded-full flex items-center justify-center shadow-2xl transform hover:scale-110 transition-transform duration-300">
                      <IconComponent className="w-16 h-16 text-white" strokeWidth={2} />
                    </div>
                    <div className="absolute -top-2 -right-2 w-10 h-10 bg-amber-500 rounded-full flex items-center justify-center font-bold text-slate-900 shadow-lg">{step.number}</div>
                  </div>
                </div>
                <div className="flex-1 bg-white rounded-2xl shadow-xl p-8 hover:shadow-2xl transition-all duration-300 border border-gray-100">
                  <h3 className="text-2xl md:text-3xl font-bold text-indigo-700 mb-2">{step.title}</h3>
                  <p className="text-lg text-slate-900 font-semibold mb-4">{step.headline}</p>
                  <p className="text-slate-700 mb-6 leading-relaxed">{step.description}</p>
                  <div className="bg-gradient-to-r from-green-50 to-indigo-50 border-l-4 border-green-500 rounded-lg p-4 mb-4">
                    <p className="text-sm font-semibold text-slate-600 mb-1">📈 OUTCOME:</p>
                    <p className="text-slate-900 font-semibold">{step.outcome}</p>
                  </div>
                  <div className="bg-gradient-to-r from-indigo-50 to-amber-50 border-l-4 border-indigo-500 rounded-lg p-4">
                    <p className="text-sm font-semibold text-slate-600 mb-1">📊 TRACKING:</p>
                    <p className="text-slate-900">{step.tracking}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="text-center mt-16 scroll-reveal">
          <button onClick={() => window.location.href = '/contact'} className="cta-secondary">Start Your Journey →</button>
        </div>
      </div>
    </section>
  );
};

export default SuccessFrameworkSection;
